import { DateTime } from "luxon";

/**
 * The hour at which an operational day begins for Line Maintenance.
 * Day shift: 06:00–18:00 | Night shift: 18:00–06:00 (next calendar day).
 */
const OPERATIONAL_DAY_START_HOUR = 6;

/**
 * Returns the start-of-day DateTime for the operational day a given timestamp belongs to.
 *
 * Events between 00:00 and 05:59 are part of the previous day's night shift,
 * so they are mapped to the previous calendar day.
 * Events from 06:00 onward stay on their calendar day.
 *
 * Examples (assuming local time):
 *   2026-04-22 02:30  → 2026-04-21 00:00  (night shift of Apr 21)
 *   2026-04-22 05:59  → 2026-04-21 00:00  (night shift of Apr 21)
 *   2026-04-22 06:00  → 2026-04-22 00:00  (day shift of Apr 22)
 *   2026-04-21 19:00  → 2026-04-21 00:00  (night shift start, still Apr 21)
 */
export const getOperationalDay = (dt: DateTime): DateTime => {
  if (!dt.isValid) return dt;
  return dt.hour < OPERATIONAL_DAY_START_HOUR
    ? dt.minus({ days: 1 }).startOf('day')
    : dt.startOf('day');
};

export const formatDueDate = (dueDate?: Date | string, mxiFormat: string = 'dd.LL.yyyy HH:mm'): string => {
  if (!dueDate) {
    return 'No due date available';
  }
  
  // Handle Date objects or date strings
  let luxonDate: DateTime;
  if (typeof dueDate === 'string') {
    // Try ISO format first, then fall back to MXI format
    luxonDate = DateTime.fromISO(dueDate);
    if (!luxonDate.isValid) {
      luxonDate = DateTime.fromFormat(dueDate, mxiFormat);
    }
  } else {
    luxonDate = DateTime.fromJSDate(new Date(dueDate));
  }
  
  // Check if the date is invalid
  if (!luxonDate.isValid) {
    return 'No due date available';
  }
  
  // Check if the date is '01.01.0001 00:00'
  const formattedDate = luxonDate.toFormat('dd.MM.yyyy HH:mm');
  if (formattedDate === '01.01.0001 00:00') {
    return 'No due date available';
  }
  
  return formattedDate;
};

/**
 * Converts a decimal number of hours into a H:MM formatted string.
 * Returns empty string for null, undefined, or 0.
 * Examples: 1.5 → "1:30", 12.2 → "12:12", 1.999 → "2:00"
 */
export const formatManHours = (hours: number | null | undefined): string => {
  if (hours == null || hours === 0) return '';
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${m.toString().padStart(2, '0')}`;
};

export const parseDateTimeToTimestamp = (dateTimeText?: string): number => {
  if (!dateTimeText) {
    return Number.POSITIVE_INFINITY;
  }

  const normalized = dateTimeText.includes('T') ? dateTimeText : dateTimeText.replace(' ', 'T');
  let dateTime = DateTime.fromISO(normalized, { zone: 'utc' });

  if (!dateTime.isValid) {
    dateTime = DateTime.fromSQL(dateTimeText, { zone: 'utc' });
  }

  return dateTime.isValid ? dateTime.toMillis() : Number.POSITIVE_INFINITY;
};

export const formatIcelandicDateTime = (dateTimeText?: string, fallbackText: string = 'No start time'): string => {
  if (!dateTimeText) {
    return fallbackText;
  }

  const normalized = dateTimeText.includes('T') ? dateTimeText : dateTimeText.replace(' ', 'T');
  let dateTime = DateTime.fromISO(normalized, { zone: 'utc' });

  if (!dateTime.isValid) {
    dateTime = DateTime.fromSQL(dateTimeText, { zone: 'utc' });
  }

  return dateTime.isValid ? dateTime.toFormat('dd.MM.yyyy HH:mm') : fallbackText;
};
